import React, { useCallback } from "react";
import { Modal, Row, Col, Form } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import {
	Button,
	CloseButton,
	HookFormInputGroup,
	HookFormRadioButton,
} from "components";
import { ManualWithholdTypes } from "types";

import { WithholdMerchantForm } from "./withholdMerchantForm";
import { WithholdTotalForm } from "./withholdTotalForm";

import styles from "../paymentsModal.module.scss";

type PaymentsWithholdModalProps = {
	show: boolean;
	onHide: () => void;
	handleWithholdTotal: Function;
	handleWithholdMerchant: Function;
	initialValues?: { [key: string]: any };
	isEditing?: boolean;
	paidYear: string;
	paidMonth: string;
};

const PaymentsWithholdModal = ({
	show,
	onHide,
	handleWithholdTotal,
	handleWithholdMerchant,
	initialValues,
	isEditing = false,
	paidYear,
	paidMonth,
}: PaymentsWithholdModalProps) => {
	const formMethods = useForm({
		mode: "all",
		reValidateMode: "onChange",
		defaultValues: {
			withholdResidualType: ManualWithholdTypes.Agent.name,
			merchantValues: "",
			is_status_update: "true",
			...initialValues,
		},
	});
	const { register, handleSubmit, watch, formState } = formMethods;

	const withholdResidualType = watch("withholdResidualType");

	const onSubmit = useCallback(
		async values => {
			const formValues = {
				...values,
				withholdId: initialValues?.withholdId,
			};
			try {
				withholdResidualType === ManualWithholdTypes.Agent.name
					? handleWithholdTotal(formValues)
					: handleWithholdMerchant(formValues);
			} finally {
				onHide();
			}
		},
		[
			withholdResidualType,
			handleWithholdTotal,
			handleWithholdMerchant,
			initialValues,
			onHide,
		],
	);

	return (
		<Modal
			show={show}
			onHide={onHide}
			animation={false}
			centered
			scrollable
			id="withholdTotalModal"
		>
			<FormProvider {...formMethods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Modal.Header className="pb-2">
						<Modal.Title className="text-center">
							Withhold Residual
						</Modal.Title>
						<CloseButton onClick={onHide} />
					</Modal.Header>
					<Modal.Body style={{ maxHeight: "75vh" }}>
						<div className={styles.withholdForm}>
							<Form.Group as={Row}>
								<Col>
									<HookFormRadioButton
										register={register}
										id="withholdResidualTotal"
										name="withholdResidualType"
										value={ManualWithholdTypes.Agent.name}
										label={
											ManualWithholdTypes.Agent
												.displayName
										}
										disabled={isEditing}
									/>
								</Col>
								<Col>
									<HookFormRadioButton
										register={register}
										id="withholdResidualMerchant"
										name="withholdResidualType"
										value={
											ManualWithholdTypes.Merchant.name
										}
										label={
											ManualWithholdTypes.Merchant
												.displayName
										}
										disabled={isEditing}
									/>
								</Col>
							</Form.Group>

							{withholdResidualType ===
							ManualWithholdTypes.Agent.name ? (
								<WithholdTotalForm
									paidMonth={paidMonth}
									paidYear={paidYear}
									isEditing={isEditing}
									initialValues={initialValues}
								/>
							) : (
								<WithholdMerchantForm
									paidMonth={paidMonth}
									paidYear={paidYear}
									isEditing={isEditing}
									initialValues={initialValues}
								/>
							)}
							<HookFormInputGroup
								register={register}
								name="withholdId"
								readOnly
								type="hidden"
							/>
						</div>
					</Modal.Body>
					<Modal.Footer className="justify-content-center pb-4">
						<Button
							type="submit"
							value="withhold"
							disabled={!formState.isValid}
						/>
					</Modal.Footer>
				</form>
			</FormProvider>
		</Modal>
	);
};

export default PaymentsWithholdModal;
